import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search,
  Grid3x3,
  List,
  Table,
  ChevronRight,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from 'wasp/client/auth';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from 'wasp/client/operations';
import { getOrganizationBySlug, getUnreadNotificationCount } from 'wasp/client/operations';
import { UserDropdown } from '../../user/UserDropdown';
import DarkModeSwitcher from '../../client/components/DarkModeSwitcher';
import { useLocationContext, LocationWithHierarchy } from '../../client/contexts/LocationContext';
import { LayoutConfig } from './types';
import { useOrgNavMode } from '../../client/contexts/OrgNavModeContext';
import { useTopBarContent } from '../../client/contexts/TopBarContentContext';
import logo from '../../client/static/App-ca.png';
import ReportButton from '../../client/components/ReportButton';
import LocationDropdown from '../../client/components/Sidebar/LocationDropdown';
import { UnreadNotificationBell } from '../notifications/UnreadNotificationBell';
import { HUB_LANDING_PATH, isHubLandingPath } from '../../shared/hubLocations';
import { HubTopBarDate } from '../../gedoba/hub-overview/HubTopBarDate';

const LOCATION_SECTIONS = new Set([
  'about', 'notices', 'events', 'tenants', 'food-court', 'facilities', 'services',
  'buy-sell', 'request-offer', 'lost-found', 'transportation', 'information-desk',
]);

/** First path segments that are app routes, not location slugs */
const RESERVED_FIRST_SEGMENTS = new Set([
  'dashboard', 'admin', 'login', 'signup', 'search', 'account', 'notifications',
  'org', 'organizations', 'organization-types', 'locations', 'pricing', 'checkout',
  'about', 'privacy', 'terms', 'contact', 'roadmap', 'pending-approval',
  'email-verification', 'password-reset', 'request-password-reset', 'oauth', 'file-upload',
  'notices', 'events', 'facilities', 'tenants', 'buy-sell', 'food-court', 'lost-found',
  'services', 'information-desk', 'request-offer', 'transportation', 'meet-the-team',
  'p', 'tools', 'test-dashboard', 'reception',
]);

/** Navigable tenant locations from taxonomy (excludes Canada / hierarchy-only nodes). */
function getSwitchableLocations(locations: LocationWithHierarchy[]): LocationWithHierarchy[] {
  return locations
    .filter((loc) => loc.slug && loc.locationCode !== 'CA')
    .sort((a, b) => a.name.localeCompare(b.name));
}

function getLocationSlugFromPath(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);
  if (!segments.length) return null;
  const first = segments[0];
  if (RESERVED_FIRST_SEGMENTS.has(first)) return null;
  if (segments.length === 1) return first;
  if (segments.length >= 2 && LOCATION_SECTIONS.has(segments[1])) return first;
  return null;
}

interface CommonTopBarProps {
  isSidebarOpen: boolean;
  onSidebarToggle: () => void;
  config: LayoutConfig;
  activeView?: 'cards' | 'list' | 'table';
  onViewChange?: (view: 'cards' | 'list' | 'table') => void;
}

function CommonTopBar({
  isSidebarOpen,
  onSidebarToggle,
  config,
  activeView = 'cards',
  onViewChange
}: CommonTopBarProps) {
  const { data: user } = useAuth();
  const { selectedLocation, setSelectedLocation, locations } = useLocationContext() as any as {
    selectedLocation: LocationWithHierarchy | null;
    setSelectedLocation: (l: LocationWithHierarchy) => void;
    locations: LocationWithHierarchy[];
  };
  const { setOrgTypeKey } = useOrgNavMode();
  const { topBarContent: topBarContentFromCtx } = useTopBarContent();
  const navigate = useNavigate();
  const selectedLocationRef = useRef(selectedLocation);
  selectedLocationRef.current = selectedLocation;
  const routerLocation = useLocation();
  const isMainLandingPage = isHubLandingPath(routerLocation.pathname);
  const isAdminPage = routerLocation.pathname.startsWith('/admin');
  const showLocationInHeader = config.showLocationDropdown !== false && !isAdminPage;
  const locationSlugFromPath = getLocationSlugFromPath(routerLocation.pathname);

  // Detect org page context from pathname (useParams not available here — shared layout)
  // Matches both /organizations/:slug/... and /:slug/... (e.g. /bayviewyards/information-desk)
  const orgPathMatch =
    routerLocation.pathname.match(/^\/organizations\/([^/]+)/) ||
    routerLocation.pathname.match(/^\/([a-z][a-z0-9-]+)\//);
  const orgSlugFromPath = orgPathMatch ? orgPathMatch[1] : undefined;
  const slugForOrgLookup = orgSlugFromPath ?? locationSlugFromPath ?? undefined;

  const { data: orgData } = useQuery(
    getOrganizationBySlug,
    { slug: slugForOrgLookup! },
    { enabled: !!slugForOrgLookup }
  ) as { data: any };

  const switchableLocations = useMemo(
    () => getSwitchableLocations(locations ?? []),
    [locations]
  );

  /** Date (or custom leading content) + divider + location on the left for all hub pages. */
  const showHubTopBarLeading = showLocationInHeader;
  const leadingTopBarContent =
    topBarContentFromCtx ?? (showHubTopBarLeading ? <HubTopBarDate /> : null);

  const { data: unreadData } = useQuery(getUnreadNotificationCount, {}, {
    enabled: !!user,
    refetchInterval: 15_000,
  });
  const unreadCount = unreadData?.count ?? 0;
  const hasUnread = unreadCount > 0;

  // Derive org type key directly from fetched org data — works on all sub-pages, not just the profile page
  const derivedOrgTypeKey: string | null = orgData?.organizationType?.key ?? null;

  // Keep context in sync so CommonSidebar can also react
  useEffect(() => {
    setOrgTypeKey(derivedOrgTypeKey);
  }, [derivedOrgTypeKey, setOrgTypeKey]);

  // Keep selected location in sync with the URL slug (taxonomy locations, not organizations)
  useEffect(() => {
    if (!locationSlugFromPath || !locations?.length) return;
    const match =
      switchableLocations.find((l) => l.slug === locationSlugFromPath) ??
      locations.find((l) => l.locationCode?.toLowerCase() === locationSlugFromPath);
    if (match && selectedLocationRef.current?.id !== match.id) {
      setSelectedLocation(match);
    }
  }, [locationSlugFromPath, locations, switchableLocations, setSelectedLocation]);

  // When on an org page, sync the most specific org location into the location context
  useEffect(() => {
    if (!orgData || !locations?.length || locationSlugFromPath) return;

    // Strategy 1: pick from locationOrganizations — primary first, then most specific (highest hierarchyLevel)
    const orgLocations: Array<{ id: string; hierarchyLevel: number; locationCode: string; isPrimary: boolean }> =
      (orgData.locationOrganizations ?? [])
        .map((lo: any) => ({ ...lo.location, isPrimary: lo.isPrimary ?? false }))
        .filter((l: any) => l?.locationCode && l.hierarchyLevel != null);

    if (orgLocations.length > 0) {
      orgLocations.sort((a, b) => {
        if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
        return b.hierarchyLevel - a.hierarchyLevel;
      });
      const mostSpecific = orgLocations[0];
      if (selectedLocationRef.current?.locationCode === mostSpecific.locationCode) return;
      const match = locations.find((l) => l.locationCode === mostSpecific.locationCode);
      if (match) { setSelectedLocation(match); return; }
    }

    // Strategy 2: fallback — match org.city or org.province string fields
    // (covers national orgs that have address strings but no LocationOrganization links)
    if (orgData.city) {
      const cityMatch = (locations as any[]).find(
        (l) => l.hierarchyLevel === 2 && l.name.toLowerCase() === orgData.city.toLowerCase()
      );
      if (cityMatch) {
        if (selectedLocationRef.current?.id === cityMatch.id) return;
        setSelectedLocation(cityMatch);
        return;
      }
    }
    if (orgData.province) {
      const provMatch = (locations as any[]).find(
        (l) => l.hierarchyLevel === 1 && l.name.toLowerCase() === orgData.province.toLowerCase()
      );
      if (provMatch) {
        if (selectedLocationRef.current?.id === provMatch.id) return;
        setSelectedLocation(provMatch);
      }
    }
  }, [orgData, locations]);
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1024;
  });
  useEffect(() => {
    const handleResize = () => setIsCompact(window.innerWidth < 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const renderHeaderLocation = (dropdownClassName?: string) => {
    if (!showLocationInHeader) return null;
    return <LocationDropdown className={dropdownClassName ?? 'max-w-full min-w-0'} />;
  };

  const headerLocationDesktop = renderHeaderLocation();
  const headerLocationMobile = renderHeaderLocation('max-w-full min-w-0 justify-center text-center');
  const hasHeaderLocation = showLocationInHeader;

  return (
    <header
      className={cn(
        "fixed top-0 right-0 bg-header-bg border-b border-header-border transition-all duration-300",
        isSidebarOpen ? "lg:left-[var(--sidebar-width)]" : "left-0",
        isSidebarOpen ? "z-50 lg:z-30" : "z-30"
      )}
      style={{
        height: 'var(--header-height)',
        padding: '0.75rem 1rem',
        minHeight: '3.5rem',
        maxHeight: '5rem'
      }}
    >
      {isCompact ? (
        /* ── Mobile top bar: logo (→ reception) | location (center) | notifications ── */
        <div className="grid grid-cols-[auto_1fr_auto] items-center w-full gap-2">
          <Link to={HUB_LANDING_PATH} className="flex-shrink-0" title="Invest Ottawa home">
            <img src={logo} alt="Invest Ottawa" className="h-7 w-auto" />
          </Link>

          {hasHeaderLocation ? (
            <div className="flex min-w-0 justify-center overflow-hidden px-1">
              {headerLocationMobile}
            </div>
          ) : (
            <div />
          )}

          <div className="flex items-center justify-end flex-shrink-0">
            {user ? (
              (config.showNotificationBell !== false) && (
                <Link
                  to="/notifications"
                  className="relative p-2 hover:bg-sidebar-hover rounded-lg transition-colors group"
                  title={hasUnread ? `${unreadCount} unread notifications` : 'Notifications'}
                >
                  <UnreadNotificationBell hasUnread={hasUnread} />
                </Link>
              )
            ) : (
              <Link
                to="/login"
                className="px-3 py-1.5 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="flex w-full min-w-0 items-center gap-3">
          {/* Left — date/breadcrumbs; location moves here when date is shown */}
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {(user || isMainLandingPage) && !isSidebarOpen && (
              <button
                onClick={onSidebarToggle}
                className="p-1.5 hover:bg-sidebar-hover rounded-lg transition-colors flex-shrink-0"
                title="Expand sidebar"
              >
                <ChevronRight className="w-5 h-5 text-header-text-muted" />
              </button>
            )}

            {leadingTopBarContent && (
              <div className="flex min-w-0 items-center gap-2 shrink">
                {leadingTopBarContent}
              </div>
            )}

            {showHubTopBarLeading && hasHeaderLocation && (
              <div className="min-w-0 max-w-[min(16rem,32vw)] border-l border-header-border pl-3 shrink">
                {headerLocationDesktop}
              </div>
            )}
          </div>

          {/* Right — fixed width; never overlapped by location title */}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {config.showViewSwitcher && onViewChange && (
              <div className="flex items-center gap-1 bg-button-bg p-1 rounded-lg">
                <button
                  onClick={() => onViewChange('cards')}
                  className={cn(
                    'p-2 rounded transition-colors',
                    activeView === 'cards' 
                      ? 'bg-content-bg shadow-sm' 
                      : 'hover:bg-button-bg-hover'
                  )}
                  title="Cards view"
                >
                  <Grid3x3 className="w-4 h-4 text-content-text" />
                </button>
                <button
                  onClick={() => onViewChange('list')}
                  className={cn(
                    'p-2 rounded transition-colors',
                    activeView === 'list' 
                      ? 'bg-content-bg shadow-sm' 
                      : 'hover:bg-button-bg-hover'
                  )}
                  title="Detail list view"
                >
                  <List className="w-4 h-4 text-content-text" />
                </button>
                <button
                  onClick={() => onViewChange('table')}
                  className={cn(
                    'p-2 rounded transition-colors',
                    activeView === 'table' 
                      ? 'bg-content-bg shadow-sm' 
                      : 'hover:bg-button-bg-hover'
                  )}
                  title="Table view"
                >
                  <Table className="w-4 h-4 text-content-text" />
                </button>
              </div>
            )}

            {config.showSearchBar && (
              <button 
                onClick={() => {
                  const from = routerLocation.pathname === '/search' ? (routerLocation.state as any)?.from : (routerLocation.pathname + routerLocation.search);
                  navigate('/search', { state: { from } });
                }}
                className="p-2 hover:bg-sidebar-hover rounded-lg transition-colors" 
                title="Search"
              >
                <Search className="w-5 h-5 text-header-text-muted" />
              </button>
            )}

            <DarkModeSwitcher />

            {user && <ReportButton />}

            {user && (config.showNotificationBell !== false) && (
              <Link 
                to="/notifications"
                className="relative p-1.5 hover:bg-sidebar-hover rounded-lg transition-colors group"
                title={hasUnread ? `${unreadCount} unread notifications` : 'Notifications'}
              >
                <UnreadNotificationBell hasUnread={hasUnread} />
              </Link>
            )}

            {user ? (
              <UserDropdown user={user} />
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 text-base font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
              >
                Login
              </Link>
            )}
          </div>
        </div>
      )}

    </header>
  );
}

// Memoize to prevent unnecessary re-renders
export default React.memo(CommonTopBar, (prevProps, nextProps) => {
  return (
    prevProps.isSidebarOpen === nextProps.isSidebarOpen &&
    prevProps.config === nextProps.config &&
    prevProps.activeView === nextProps.activeView
  );
});



